export const allowedSpans = [
  'NextServer.getServerRequestHandler',
  'NextServer.getRequestHandler',
  'LoadComponents.loadComponents',
  'NextNodeServer.findPageComponents',
  'BaseServer.handleRequest',
  'BaseServer.renderToResponseWithComponents',
  'BaseServer.renderToResponse',
  'BaseServer.pipe',
  'BaseServer.render',
  'AppRender.fetch',
  'AppRender.getBodyResult',
];

export enum MetricLayers {
  HandleRequest = 'HandleRequest',
  RenderRoute = 'RenderRoute',
  ServerFetch = 'ServerFetch',
  GenerateMetadata = 'GenerateMetadata',
  GetServerRequestHandler = 'GetServerRequestHandler',
  GetRequestHandler = 'GetRequestHandler',
  LoadComponents = 'LoadComponents',
  FindPageComponents = 'FindPageComponents',
  RenderToResponseWithComponents = 'RenderToResponseWithComponents',
  RenderToResponse = 'RenderToResponse',
  Pipe = 'Pipe',
  Render = 'Render',
}

/**
 * These are our custom layers for stdv1 metrics collection.
 *
 * You can see these as analogous to controller/service/client layers
 * we use in other services.
 */
export const spanTypeToLayer: Record<string, MetricLayers> = {
  'BaseServer.handleRequest': MetricLayers.HandleRequest,
  'AppRender.getBodyResult': MetricLayers.RenderRoute,
  'AppRender.fetch': MetricLayers.ServerFetch,
  'ResolveMetadata.generateMetadata': MetricLayers.GenerateMetadata,
  'NextServer.getServerRequestHandler': MetricLayers.GetServerRequestHandler,
  'NextServer.getRequestHandler': MetricLayers.GetRequestHandler,
  'LoadComponents.loadComponents': MetricLayers.LoadComponents,
  'NextNodeServer.findPageComponents': MetricLayers.FindPageComponents,
  'BaseServer.renderToResponseWithComponents':
    MetricLayers.RenderToResponseWithComponents,
  'BaseServer.renderToResponse': MetricLayers.RenderToResponse,
  'BaseServer.pipe': MetricLayers.Pipe,
  'BaseServer.render': MetricLayers.Render,
};

/**
 * When adding a new page, you need to define the classFunctionName here
 * so we have a mapping, which makes defining overrides easier and in
 * general makes working with the dashboards easier.
 *
 * You can find out the correct route pattern for your page by setting
 * debug to true in the StatsDExporter class.
 *
 * If not added, a default will be computed from the path.
 */
export const routeToClassFnName: Record<string, string> = {
  '/[locale]': 'localisedHomePage',
  '/[locale]/sell': 'sellOnDepopPage',
  '/[locale]/bag': 'bagPage',
  '/[locale]/password-reset': 'passwordResetPage',
  '/[locale]/password-reset/[token]': 'passwordResetTokenPage',
  '/[locale]/sellinghub/shipping': 'sellingHubShippingPage',
  '/[locale]/sellinghub/stats': 'sellingHubStatsPage',
  '/[locale]/sellinghub/selling/active': 'sellingHubSellingActivePage',
  '/[locale]/sellinghub/selling/sold': 'sellingHubSellingSoldPage',
  '/[locale]/sellinghub/boost/all': 'sellingHubBoostAllPage',
  '/[locale]/sellinghub/boost/boosted': 'sellingHubBoostBoostedPage',
  '/[locale]/sellinghub/drafts/readytopost': 'sellingHubDraftsReadyToPostPage',
  '/[locale]/sellinghub/drafts/incomplete': 'sellingHubDraftsIncompletePage',
  '/[locale]/sellinghub/drafts': 'sellingHubBulkListingPage',
  '/[locale]/sellinghub/drafts/edit/[draftId]': 'sellingHubEditDraftPage',
  '/[locale]/sellinghub/offers': 'sellingHubOffersPage',
  '/[locale]/sellinghub/offers/[productId]': 'sellingHubOffersViewPage',
  '/[locale]/sellinghub/vacation': 'sellingHubVacationPage',
  '/[locale]/sellinghub/bulklisting': 'sellingHubCSVBulkListingPage',
  '/[locale]/[username]': 'shopPage',
  '/[locale]/[username]/likes': 'shopLikesPage',
  '/[locale]/[username]/saved': 'shopSavedPage',
  '/[locale]/[username]/drop': 'shopDropPage',
  '/[locale]/search': 'searchPage',
  '/[locale]/brands/a-z': 'brandsAtoZPage',
  '/[locale]/brands/a-z/[letter]': 'brandsLetterPage',
  '/[locale]/login': 'loginPage',
  '/[locale]/login/mfa': 'loginMFAPage',
  '/[locale]/login/magic-link': 'requestMagicLinkPage',
  '/[locale]/login/magic-link/check-inbox': 'magicLinkCheckInboxPage',
  '/[locale]/login/magic-link/invalid-link': 'magicLinkInvalidLinkPage',
  '/[locale]/login/magic-link/missing-link': 'magicLinkInvalidLinkPage',
  '/[locale]/login/magic-link/verify/[token]': 'magicLinkVerifyPage',
  '/[locale]/signup': 'signupPage',
  '/[locale]/signup/apple': 'signupApplePage',
  '/[locale]/signup/ebay': 'signupEbayPage',
  '/[locale]/signup/google': 'signupGooglePage',
  '/[locale]/start-selling/dob': 'sellerOnboardingDateOfBirthPage',
  '/[locale]/start-selling/billing-address':
    'sellerOnboardingBillingAddressPage',
  '/[locale]/start-selling/depop-balance': 'sellerOnboardingDepopBalancePage',
  '/[locale]/start-selling/paypal': 'sellerOnboardingPaypalPage',
  '/[locale]/start-selling/company': 'sellerOnboardingCompanyPage',
  '/[locale]/start-selling/stripe/account-verification/failure':
    'sellerOnboardingStripeSuccessPage',
  '/[locale]/start-selling/stripe/account-verification/success':
    'sellerOnboardingStripeFailurePage',
  '/[locale]/partners/tony-hawk': 'tonyHawkPage',
  '/[locale]/products/[slug]': 'productPage',
  '/[locale]/products/[slug]/manage': 'productManagePage',
  '/[locale]/products/create': 'productCreatePage',
  '/[locale]/products/create/copy': 'productCreateCopyPage',
  '/[locale]/products/create/first': 'productCreateFirstPage',
  '/[locale]/products/create/repop': 'productCreateRepopPage',
  '/[locale]/products/create/success': 'productCreateSuccessPage',
  '/[locale]/products/edit/[slug]/': 'productEditPage',
  '/[locale]/(browse)/brands/[slug]': 'browseBrandsPage',
  '/[locale]/(browse)/brands/[slug]/c/[...category]':
    'browseBrandsCategoryPage',
  '/[locale]/(browse)/category/[...category]': 'browseCategoryPage',
  '/[locale]/(browse)/explore': 'browseExplorePage',
  '/[locale]/(browse)/theme': 'browseThemePage',
  '/[locale]/pay': 'checkoutLandingPage',
  '/[locale]/pay/return': 'checkoutReturnPage',
  '/[locale]/pay/success': 'checkoutSuccessPage',
  '/[locale]/pay/failure': 'checkoutFailurePage',
  '/[locale]/settings': 'settingsPage',
  '/[locale]/settings/email_preferences': 'emailPreferencesPage',
  '/[locale]/settings/seller_preferences': 'sellerPreferencesPage',
  '/[locale]/settings/push_preferences': 'pushPreferencesPage',
  '/[locale]/marketing/unsubscribe': 'marketingUnsubscribePage',
  '/[locale]/purchases': 'purchasesPage',
  '/[locale]/campaign/sell': 'campaignSellPage',
  '/[locale]/disputes': 'disputesPage',
  '/[locale]/disputes/initiate': 'disputeInitiatePage',
  '/[locale]/disputes/initiate/[purchaseId]': 'disputeInitiateForPurchasePage',
  '/[locale]/disputes/[disputeId]': 'disputeDetailsPage',
  '/[locale]/shipping/': 'shippingPage',
  '/[locale]/quiz[slug]': 'quizPage',
  '/[locale]/start-to-sell/': 'startToSellPage',
  '/[locale]/ebay': 'ebayLandingPage',
  '/[locale]/ebay/sell': 'ebaySellLandingPage',
  '/[locale]/likes': 'guestLikesPage',
};

/* Key and Value *must* be the same, apart from that these can be whatever you want */
export enum Stdv1Stat {
  'cookie.trackers.init.failed' = 'cookie.trackers.init.failed',
  'performance.tracking.failed' = 'performance.tracking.failed',
  'canary.metric.server.success' = 'canary.metric.server.success',
  'canary.metric.client.success' = 'canary.metric.client.success',
  'errors.errorBoundary.caught' = 'errors.errorBoundary.caught',
  'errors.http.networkError' = 'errors.http.networkError',
  'errors.http.urlError' = 'errors.http.urlError',
  'errors.user.parse.failed' = 'errors.user.parse.failed',
  'magic_link.tracking.login.success' = 'magic_link.tracking.login.success',
  'magic_link.tracking.login.browser_matched' = 'magic_link.tracking.login.browser_matched',
  'magic_link.tracking.login.browser_not_matched' = 'magic_link.tracking.login.browser_not_matched',
  'magic_link.tracking.redirect.checkout.has_products.success' = 'magic_link.tracking.redirect.checkout.has_products.success',
  'magic_link.tracking.redirect.checkout.has_products.failure' = 'magic_link.tracking.redirect.checkout.has_products.failure',
  'signup.signup_request.email_error' = 'signup.signup_request.email_error',
  'signup.signup_request.password_error' = 'signup.signup_request.password_error',
  'signup.signup_request.username_error' = 'signup.signup_request.username_error',
  'signup.signup_request.unknown_error' = 'signup.signup_request.unknown_error',
  'signup.signup_request.other_error' = 'signup.signup_request.other_error',
  'signup.signup_request.dob_error' = 'signup.signup_request.dob_error',
  'experiments.preview_request.prefetch.failed' = 'experiments.preview_request.prefetch.failed',
  'experiments.preview_request.prefetch.success' = 'experiments.preview_request.prefetch.success',
  'apple.auth.user_trigger_new_signin_flow_error' = 'apple.auth.user_trigger_new_signin_flow_error',
  'apple.auth.popup_closed_by_user_error' = 'apple.auth.popup_closed_by_user_error',
  'ebay.auth.popup_closed_by_user_error' = 'ebay.auth.popup_closed_by_user_error',
  'ebay.auth.state_mismatch_error' = 'ebay.auth.state_mismatch_error',
  'ebay.auth.authorize_error' = 'ebay.auth.authorize_error',
  'ebay.auth.credentials_error' = 'ebay.auth.credentials_error',
  'zendesk.auth.sso_error' = 'zendesk.auth.sso_error',
  'experiment.search.explore_redirect_success' = 'experiment.search.explore_redirect_success',
  'web.checkout.gmv.total' = 'web.checkout.gmv.total',
  'pdp.gb.marketplace_fee_shown' = 'pdp.gb.marketplace_fee_shown',
  'pdp.gb.marketplace_fee_not_shown' = 'pdp.gb.marketplace_fee_not_shown',
  'pdp.us.marketplace_fee_shown' = 'pdp.us.marketplace_fee_shown',
  'pdp.us.marketplace_fee_not_shown' = 'pdp.us.marketplace_fee_not_shown',
  'pdp.gb.upfront_shipping_shown' = 'pdp.gb.upfront_shipping_shown',
  'pdp.gb.upfront_shipping_not_shown' = 'pdp.gb.upfront_shipping_not_shown',
  'pdp.us.upfront_shipping_shown' = 'pdp.us.upfront_shipping_shown',
  'pdp.us.upfront_shipping_not_shown' = 'pdp.us.upfront_shipping_not_shown',
  'pdp.ct.upfront_shipping_shown' = 'pdp.ca.upfront_shipping_shown',
  'pdp.ct.upfront_shipping_not_shown' = 'pdp.ca.upfront_shipping_not_shown',
  'offers.ct.upfront_shipping_shown' = 'offers.ct.upfront_shipping_shown',
  'offers.ct.upfront_shipping_not_shown' = 'offers.ct.upfront_shipping_not_shown',
  'offers.ny.upfront_shipping_shown' = 'offers.ny.upfront_shipping_shown',
  'offers.ny.upfront_shipping_not_shown' = 'offers.ny.upfront_shipping_not_shown',
  'outfits.collage.save.duration' = 'outfits.collage.save.duration',
  'outfits.collage.create.slow' = 'outfits.collage.create.slow',
  'outfits.collage.create.started' = 'outfits.collage.create.started',
  'outfits.collage.create.failed' = 'outfits.collage.create.failed',
  'outfits.collage.create.request.finished' = 'outfits.collage.create.request.finished',
  'turnstile.challenge.success' = 'turnstile.challenge.success',
  'turnstile.challenge.failed' = 'turnstile.challenge.failed',
  'cms.contentful.request.finished' = 'cms.contentful.request.finished',
  'cms.contentful.request.duration' = 'cms.contentful.request.duration',
  'cms.contentful.request.failed' = 'cms.contentful.request.failed',
  'cms.contentful.request.rate_limited' = 'cms.contentful.request.rate_limited',
  'cms.contentful.request.timeout' = 'cms.contentful.request.timeout',
  'cms.contentful.request.unauthorized' = 'cms.contentful.request.unauthorized',
  'cms.contentful.request.retried' = 'cms.contentful.request.retried',
  'cms.contentful.rate_limit.second_limit' = 'cms.contentful.rate_limit.second_limit',
  'cms.contentful.read.fell_back' = 'cms.contentful.read.fell_back',
}

// Shared by the RUM stream and the browser log stream.
export const IGNORED_TRANSPORT_MESSAGES = [
  'Network Error',
  'NetworkError',
  'Failed to fetch',
  'Load failed',
  'chunk',
  'Connection closed.',
];

// Matched against a RUM error's message text.
export const IGNORED_MESSAGE_PHRASES = [
  ...IGNORED_TRANSPORT_MESSAGES,
  'getComputedStyle',
  'UET',
  'AxiosError',
  'csp_violation',
  'Script error',
  'eval',
  'ResizeObserver',
  '_AutofillCallbackHandler',
  'find variable: lastScrollEventTimestamp',
  'Fetch is aborted',
  'A listener indicated an asynchronous response',
  'Zotero Connector',
];

// Matched against a RUM error's stack and handling stack.
export const IGNORED_ORIGINS = [
  'safari-extension',
  'ms-browser-extension',
  'bookmarklet',
  'safari-web-extension',
  'moz-extension',
  'get663',
  'sift',
  'chrome-extension',
  'cdn.branch',
  'pinimg',
  'IDBDatabase',
  'dbankcloud.com',
  'js.stripe.com',
  'accounts.google.com',
  'segment',
  'clarity.ms',
  'clarity.js',
  'googletagmanager',
  'd118.org',
  'fp.sn.ifl.net',
  'pinterest',
  'bing',
  'doubleclick',
  'schools-blocked',
  'facebook',
  'gstatic',
];

export const DD_SERVICE = 'web-default-website-server';

export const CF_BOT_SCORE_THRESHOLD = 30;
